import { useState } from "react";
import * as Babel from "@babel/standalone";
import ApiUrls from "../../helper/api-urls";
import ApiHelper from "../../helper/api-helper";
import { NotificationManager } from "src/util/notify";

const NO_CACHE = "no-cache, no-store, must-revalidate";
const EXPIRES_HTTP_DATE = "Thu, 01 Jan 1970 00:00:00 GMT"; 

const DocumentTranscompiler = () => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const handleFileSelection = (files) => {
        setErrorMessage("");
        setSelectedFiles([...selectedFiles, ...files]);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        handleFileSelection(Array.from(event.dataTransfer.files));
    };

    const handleFileChange = (event) => {
        handleFileSelection(Array.from(event.target.files));
    };

    const convertJSX = (jsxCode) => {
        try {
            return Babel.transform(jsxCode, {
                presets: ['react'],
                plugins: ['transform-modules-commonjs'],
            }).code;
        } catch (error) {
            console.error("JSX Conversion Error:", error);
            return null;
        }
    };

const getPresignedUrl = async ({ fileName, contentType, cacheControl, expires }) => { 
        try {
            const postData = { fileName, contentType, cacheControl, expires };
            const result = await ApiHelper.postRequest(ApiUrls.GET_S3_TEMPLATE_SIGNED_URL, postData);
            return result?.resultData?.uploadUrl || null;
        } catch (error) {
            NotificationManager.error("Failed to get S3 URL");
            return null;
        }
    };

  const uploadToS3 = async (uploadUrl, content, onProgress) => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("PUT", uploadUrl, true);

            xhr.setRequestHeader("Content-Type", "application/javascript");
            xhr.setRequestHeader("Cache-Control", NO_CACHE);
            xhr.setRequestHeader("Expires", EXPIRES_HTTP_DATE);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    const percentCompleted = Math.round((event.loaded / event.total) * 100);
                    onProgress(percentCompleted);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200) resolve(true);
                else reject(new Error(`Upload failed with status ${xhr.status}`));
            };

            xhr.onerror = () => reject(new Error("Upload failed"));
            xhr.send(content);
        });
    };

    const uploadFiles = async () => {
        if (selectedFiles.length === 0) return;

        setUploading(true);
        setUploadProgress(0);
        setSuccessMessage("");
        setUploadedFiles([]);

        let successfulUploads = [];

        for (let i = 0; i < selectedFiles.length; i++) {
            const file = selectedFiles[i];
            const reader = new FileReader();
            const contentType = "application/javascript";
            const isJSX = file.name.toLowerCase().endsWith(".jsx");
            const targetName = isJSX ? file.name.replace(/\.jsx$/i, ".js") : file.name;

            reader.onload = async (event) => {
                const jsxCode = event.target.result;
                const convertedCode = convertJSX(jsxCode);

                if (convertedCode) {
                    const uploadUrl = await getPresignedUrl({ fileName: targetName, contentType, cacheControl: NO_CACHE, expires: EXPIRES_HTTP_DATE });
                    if (uploadUrl) {
                        try {
                            await uploadToS3(uploadUrl, convertedCode, (progress) => {
                                setUploadProgress(Math.round((progress + i * 100) / selectedFiles.length));
                            });
                            successfulUploads.push(targetName);
                        } catch (error) {
                            console.error("S3 Upload Error:", error);
                        }
                    }
                }

                if (i === selectedFiles.length - 1) {
                    setUploading(false);
                    setSuccessMessage("Files converted and uploaded successfully!");
                    setUploadedFiles(successfulUploads);
                    setSelectedFiles([]);
                }
            };

            reader.readAsText(file);
        }
    };

    return (
        <div className="container text-center p-4 bg-white shadow rounded" style={{ marginTop: "50px" }}>
            <h3 className="mb-4">Document Templates Uploader</h3>
            <p class="text-center text-muted">
                Upload your JS files to be converted using Babel and stored on S3.
            </p>
            <p class="text-center text-muted">
                This tool allows you to upload React component files written in JS. Once uploaded,
                the files will be automatically converted into standard JavaScript using
                <code> React.createElement() </code> syntax. After conversion, the files will be
                uploaded to your configured <strong> Amazon S3 bucket. </strong> The converted files will be used in our UI application. Once uploaded to S3,
                you can use the files directly without building them again.
            </p>
            <div
                className="drop-zone p-4 border border-primary text-primary rounded mb-3"
                style={{ cursor: "pointer" }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => document.getElementById("fileInput").click()}
            >
                <p>Drag & Drop JS Files Here or Click to Select</p>
                <input type="file" id="fileInput" multiple accept=".js, .jsx" className="d-none" onChange={handleFileChange} />
            </div>

            {errorMessage && <p className="text-danger">{errorMessage}</p>}

            {selectedFiles.length > 0 && (
                <div className="mb-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <h5>Selected Files ({selectedFiles.length}):</h5>
                    <ul className="list-group">
                        {selectedFiles.map((file, index) => (
                            <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                {file.name}
                                <span className="badge bg-secondary">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <button className="btn btn-primary mt-3" disabled={selectedFiles.length === 0 || uploading} onClick={uploadFiles}>
                {uploading ? "Uploading..." : `Upload ${selectedFiles.length} File(s)`}
            </button>

            {uploading && (
                <div className="mt-3">
                    <div className="progress">
                        <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: `${uploadProgress}%` }}>
                            {uploadProgress}%
                        </div>
                    </div>
                    <p className="mt-2">Uploading... {uploadProgress}%</p>
                </div>
            )}

            {successMessage && (
                <div className="mt-3 text-success">
                    <p>{successMessage}</p>
                    <h5>Uploaded Files:</h5>
                    <ul className="list-group">
                        {uploadedFiles.map((file, index) => (
                            <li key={index} className="list-group-item">{file}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default DocumentTranscompiler;
